import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import EmptyState from '@/components/feature/EmptyState';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import {
  allArticles,
  filterArticles,
  getCategoryByKey,
  type SortKey,
} from '@/utils/articles';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryName = '' } = useParams();
  const category = getCategoryByKey(categoryName);

  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useDocumentMeta(
    category ? `${category.name} - 91黑料娱乐黑料爆料平台` : '分类 - 91黑料',
    category ? `${category.desc} 在91黑料查看最新${category.name}相关爆料与资讯。` : undefined,
  );

  useEffect(() => {
    setActiveTags([]);
    setSort('latest');
    setVisible(PAGE_SIZE);
  }, [categoryName]);

  const tags = useMemo(() => {
    const set = new Set<string>();
    allArticles
      .filter((a) => a.category === categoryName)
      .forEach((a) => a.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [categoryName]);

  const filtered = useMemo(
    () => filterArticles({ category: categoryName, tags: activeTags, sort }),
    [categoryName, activeTags, sort],
  );

  const shown = filtered.slice(0, visible);

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
    setVisible(PAGE_SIZE);
  };

  const resetFilters = () => {
    setActiveTags([]);
    setSort('latest');
    setVisible(PAGE_SIZE);
  };

  if (!category) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-10">
        <EmptyState
          icon="ri-file-unknow-line"
          title="该分类不存在"
          description="你访问的分类可能已调整或链接有误，试试返回首页浏览其他内容。"
          actionTo="/"
          actionLabel="返回首页"
        />
      </div>
    );
  }

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: '娱乐黑料', to: '/category/feiwen' }, { label: category.name }]} />

      <header className="mt-5 md:mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <span className="w-14 h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center shrink-0">
            <i className={`${category.icon} text-3xl`}></i>
          </span>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-foreground-950">{category.name}</h1>
            <p className="text-sm text-foreground-600 mt-2 max-w-2xl">{category.desc}</p>
          </div>
        </div>
        <div className="shrink-0 text-sm text-foreground-500">
          共 <span className="font-bold text-primary-600 font-label">{filtered.length}</span> 条爆料
        </div>
      </header>

      <div className="mt-6 flex flex-col lg:flex-row gap-6">
        <div className="hidden lg:block w-60 shrink-0">
          <FilterPanel
            tags={tags}
            activeTags={activeTags}
            onToggle={toggleTag}
            sort={sort}
            onSort={(s) => {
              setSort(s);
              setVisible(PAGE_SIZE);
            }}
            onReset={resetFilters}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
              <div className="flex items-center gap-1 px-1 py-1 bg-background-100 rounded-full">
                {(['latest', 'hot'] as SortKey[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setSort(s);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                      sort === s ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    {s === 'latest' ? '最新发布' : '最多热度'}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-100 text-foreground-800 text-sm whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i> 筛选
              {activeTags.length > 0 && (
                <span className="w-5 h-5 rounded-full bg-primary-500 text-background-50 text-xs flex items-center justify-center">
                  {activeTags.length}
                </span>
              )}
            </button>
          </div>

          {activeTags.length > 0 && (
            <div className="hidden lg:flex flex-wrap items-center gap-2 mb-4">
              {activeTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs"
                >
                  {tag}
                  <button type="button" aria-label={`移除${tag}`} onClick={() => toggleTag(tag)}>
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              ))}
              <button type="button" onClick={resetFilters} className="text-xs text-primary-600 underline">
                清空
              </button>
            </div>
          )}

          {shown.length === 0 ? (
            <EmptyState
              icon="ri-emotion-sad-line"
              title="暂无相关爆料"
              description="当前筛选条件下没有找到内容，试着调整或重置筛选条件。"
              onAction={resetFilters}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {shown.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
              {visible < filtered.length && (
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="px-6 py-2.5 rounded-md bg-background-100 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-add-line"></i> 加载更多
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-[300px] max-w-[85vw] bg-background-50 p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-black text-foreground-950">筛选与排序</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <FilterPanel
              tags={tags}
              activeTags={activeTags}
              onToggle={toggleTag}
              sort={sort}
              onSort={(s) => {
                setSort(s);
                setVisible(PAGE_SIZE);
              }}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="w-full mt-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap"
            >
              查看结果（{filtered.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface FilterPanelProps {
  tags: string[];
  activeTags: string[];
  onToggle: (tag: string) => void;
  sort: SortKey;
  onSort: (sort: SortKey) => void;
  onReset: () => void;
}

function FilterPanel({ tags, activeTags, onToggle, sort, onSort, onReset }: FilterPanelProps) {
  return (
    <div className="space-y-6">
      <div className="bg-background-50 lg:border lg:border-background-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-price-tag-3-line text-primary-500"></i>
          <h3 className="text-sm font-bold text-foreground-950">标签筛选</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => {
            const active = activeTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => onToggle(tag)}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-background-50 lg:border lg:border-background-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-sort-desc text-accent-600"></i>
          <h3 className="text-sm font-bold text-foreground-950">排序方式</h3>
        </div>
        <div className="flex flex-col gap-2">
          {([
            { key: 'latest', label: '按发布时间' },
            { key: 'hot', label: '按热度' },
          ] as { key: SortKey; label: string }[]).map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onSort(item.key)}
              className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                sort === item.key
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-background-100 text-foreground-700'
              }`}
            >
              {item.label}
              {sort === item.key && <i className="ri-check-line"></i>}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="w-full py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
      >
        重置筛选
      </button>
    </div>
  );
}